import React, { useState, HTMLAttributes } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import imageSizes from "imageSizes";
import VideoDialog from "components/dialogs/VideoDialog";
import clsx from "clsx";
import Img from "./Img/Img";
import PlayButtonOutline from "assets/svgs/icons/PlayButtonOutline";
import Glitch from "./glitch/Glitch";
import playButtonBackground from "assets/images/homePage/bannerSection/video/btn_play_bg.png";

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        root: {
            cursor: "pointer",
            position: "relative",
            "&:hover": {
                "& $playButton": { opacity: 1 },
                "& $playButtonOutline": { display: "none" },
                "& $playButtonGlitchOutline": { display: "block" },
            },
        },
        playButtonContainer: {
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: theme.palette.common.white,
        },
        playButton: {
            width: 100,
            position: "relative",
            "& img": {
                width: "100%",
            },
            opacity: 0.8,
        },
        playButtonOutline: {},
        playButtonGlitchOutline: { display: "none" },
    });
});

type ThumbnailSize = "tiny" | "small" | "medium" | "large";

interface IProps extends HTMLAttributes<HTMLDivElement> {
    videoId: string;
    isLoading?: boolean;
    thumbnailSize?: ThumbnailSize;
}

export default function YouTubeVideoDialogThumbnail(props: IProps) {
    const classes = useStyles();
    const {
        videoId,
        isLoading,
        thumbnailSize = "medium",
        className,
        children,
        onClick = () => {},
        ...rest
    } = props;

    const [videoDialogIsOpen, setVideoDialogIsOpen] = useState<boolean>(false);

    // https://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
    const getThumbnailImage = (size: ThumbnailSize) =>
        `https://img.youtube.com/vi/${videoId}/${getSizePrefix(size)}default.jpg`;

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setVideoDialogIsOpen(true);
        onClick(event);
    };

    return (
        <>
            <div className={clsx(className, classes.root)} onClick={handleClick} {...rest}>
                <Img
                    src={getThumbnailImage(thumbnailSize)}
                    placeholder={getThumbnailImage("tiny")}
                    imageProps={{ style: { objectFit: "cover" } }}
                    {...imageSizes.youtubeVideo}
                    loading={isLoading}
                />
                <div className={classes.playButtonContainer}>
                    <div className={classes.playButton}>
                        <div
                            style={{
                                width: "80%",
                                height: "100%",
                                position: "absolute",
                                top: 5,
                                left: 7,
                            }}
                        >
                            <div className={classes.playButtonOutline}>
                                <PlayButtonOutline />
                            </div>
                            <Glitch className={classes.playButtonGlitchOutline}>
                                <PlayButtonOutline />
                            </Glitch>
                        </div>
                        <img src={playButtonBackground} alt="Play button" />
                    </div>
                </div>
            </div>

            <VideoDialog
                open={videoDialogIsOpen}
                videoId={videoId}
                onClose={() => setVideoDialogIsOpen(false)}
            />
        </>
    );
}

function getSizePrefix(size: ThumbnailSize): "" | "mq" | "hq" | "maxres" {
    // https://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
    if (size === "tiny") return "";
    if (size === "small") return "mq";
    if (size === "medium") return "hq";
    if (size === "large") return "maxres";

    return "hq";
}
